import * as React from "react";
import { Box, Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppVersion } from "@/api/hooks";
import { loadedBuild } from "@/api/loadedBuild";
import { formatDateTime } from "@/i18n/format";
import { brand } from "@/theme";

/** Version + build metadata next to the product name in the AppBar.
 *  ``v0.1.0`` is rendered as a small grey pill (``brand.muted``)
 *  against the black AppBar chrome. The short commit SHA sits in
 *  muted mono to its right, and the build time is on hover via
 *  tooltip.
 *
 *  They describe the build THIS TAB runs (``loadedBuild``: baked into the
 *  bundle by CI, else the first /api/version answer), not the server's
 *  latest answer: /api/version is polled, and after a deploy the server
 *  moves on while an open tab still runs the old bundle — the
 *  maintenance banner offers the reload. /api/version is unauth'd, so the
 *  fallback works on the login screen too. */
export function VersionBadge(): React.JSX.Element {
  const { t } = useTranslation();
  const v = useAppVersion();
  const build = loadedBuild() ?? v.data;
  // Always render the pill so operators can spot a misconfigured
  // deploy at a glance. Previously the component returned null when
  // the fetch hadn't resolved (or silently failed), which made the
  // navbar look like the badge had never been added — exactly the
  // symptom reported in the screenshot. Surface the state instead.
  const version = build?.version || (v.isError ? "?" : "…");
  const commitSha = build?.commit_sha ?? "";
  const showSha = Boolean(commitSha) && commitSha !== "unknown";
  const buildTooltip = build?.built_at
    ? t("appbar.version.built_at", { when: formatDateTime(build.built_at) })
    : v.isError
      ? t("appbar.version.unreachable")
      : t("appbar.version.local_build");
  const tooltip = showSha
    ? `${buildTooltip}\n${t("appbar.version.commit", { sha: commitSha })}`
    : buildTooltip;
  return (
    <Tooltip
      title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
      arrow
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 0.75,
          ml: 0.75,
        }}
      >
        <Box
          component="span"
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 0.75,
            bgcolor: brand.muted,
            color: brand.chrome,
            fontFamily: brand.fonts.mono,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.2,
            lineHeight: 1.5,
          }}
        >
          v{version}
        </Box>
        {showSha && (
          <Box
            component="span"
            sx={{
              fontFamily: brand.fonts.mono,
              fontSize: 10.5,
              opacity: 0.7,
              letterSpacing: 0.1,
            }}
          >
            {commitSha}
          </Box>
        )}
      </Stack>
    </Tooltip>
  );
}
